import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import TaskComponent from "@/components/tasks/Task";
import {
  Task, TaskResponse,
  TaskPromptResponse,
  Project,
  TaskPrompt
} from "@/api/types";
import SimplePage from "@/components/structure/SimplePage";
import { apiClient } from "@/api";
import { PromptType, TaskPromptProps } from "@/components/tasks/types";
import { useAuth } from "@/contexts/AuthContext";
import AppLinkButton from "@/components/structure/AppLinkButton";
import RoleChips from "@/components/auth/RoleChips";
import { ProjectProvider, useProject } from "@/contexts/ProjectContext";

interface DiscussTaskHeaderProps {
  taskData?: Task | null;
  taskResponse?: TaskResponse | null;
}

export function DiscussTaskHeader({ taskData, taskResponse }: DiscussTaskHeaderProps) {
  const { project } = useProject();

  return (
        <div className="mb-4 border-blue-100 border-1 rounded p-3 bg-blue-50">
          <h1 className="text-lg flex items-center">
            <strong>Project:</strong>{" "}
            <AppLinkButton
              to={`/projects/${project?.id}/`}
              variant="primary"
              className="py-1 ml-1"
            >
              <span className="mr-2">{project?.name}</span>
            </AppLinkButton>
            <RoleChips roles={project?.roles} />
          </h1>
          <h1 className="text-lg">
            <strong>Task:</strong>{" "}
            {(taskData && `${taskData.title}`)}
          </h1>
          <div className="mt-1 text-sm text-gray-500 dark:text-gray-500">
            Your response was submitted on{" "}
            {new Date(taskResponse?.createdAt!).toLocaleString()}
          </div>
        </div>
  );
}

export default function DiscussTaskResponse() {
  const { isAuthenticated } = useAuth();
  const { taskId, taskResponseId } = useParams<{
    taskId: string;
    taskResponseId: string;
  }>();
  const [taskData, setTaskData] = useState<Task | null>(null);
  const [projectData, setProjectData] = useState<Project | null>(null);
  const [taskResponse, setTaskResponse] = useState<TaskResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchTask = async () => {
      try {
        const data = await apiClient.get<Task>(`/tasks/${taskId}/`);

        setTaskData(data);
        setTaskResponse(data.lastResponse || null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    if (taskId) {
      fetchTask();
    }
  }, [isAuthenticated, taskId]);

  // fetch the project data once the task data is loaded
  useEffect(() => {
    const fetchProject = async () => {
      try {
        const data = await apiClient.get<Project>(
          `/projects/${taskData?.project}/`
        );
        setProjectData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      }
    };
    if (taskData) {
      fetchProject();
    }
  }, [taskData]);

  let message = null;
  let taskToReview = null;

  if (loading) {
    message = <div>Loading task {taskId}...</div>;
  } else if (error) {
    message = <div>Error: {error}</div>;
  } else if (!taskData) {
    message = <div>No task data found</div>;
  } else {
    // convert prompts into TaskPromptProps format
    const prompts = taskData.prompts.map((prompt) => ({
      ...prompt,
      type: prompt.type as PromptType, // assuming type is compatible
      options: prompt.options || [], // default to empty array if no options
      value: taskResponse
        ? taskResponse.promptResponses?.find(
            (pr: TaskPromptResponse) => pr.taskPrompt === prompt.id
          )?.responseText
        : undefined,
    })) as TaskPrompt[];

    const combinedTaskData = {
      ...taskData,
      prompts: prompts,
    };

    taskToReview = (
      <TaskComponent
        id={taskId}
        task={combinedTaskData}
        project={projectData}
        readOnly={true}
        showTaskSummary={false}
        isDiscussing={true}
      />
    );
  }

  return (
    <SimplePage title="Discuss Task Responses">
      <ProjectProvider projectId={taskData?.project}>
        <DiscussTaskHeader
          taskData={taskData}
          taskResponse={taskResponse}
        />

        {message && <div className="mb-4">{message}</div>}

        {/* Render the task component */}
        {taskToReview && <div>{taskToReview}</div>}
      </ProjectProvider>
    </SimplePage>
  );
}
